import React, {useEffect, useRef, forwardRef} from 'react';
import PropTypes from 'prop-types';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const ScrollingContainer = forwardRef(
  ({children, className, direction = 'vertical', getScroller, style}, ref) => {
    const scrollerRef = useRef(null);

    // Use the forwarded ref if provided, otherwise use local ref
    const combinedRef = node => {
      scrollerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const handleWheel = event => {
      const scroller = getScroller ? getScroller() : scrollerRef.current;
      // if scroller doesn't exist bail
      if (!scroller) return;
      // pull necessary data from scroller
      const {scrollHeight, scrollTop, offsetHeight} = scroller;
      // bail if there's no overflow to scroll on.
      if (offsetHeight >= scrollHeight) return;
      // get the next scroll
      const {deltaY} = event;
      // prevent bubbling since we know we've got the chance to scroll
      event.stopPropagation();

      const nextScrollTop = scrollTop + deltaY;
      if (
        offsetHeight + nextScrollTop >= scrollHeight ||
        deltaY > offsetHeight
      ) {
        event.preventDefault();
        scroller.scrollTop = scrollHeight;
      } else if (nextScrollTop < 0 || -deltaY > offsetHeight) {
        event.preventDefault();
        scroller.scrollTop = 0;
      }
    };

    useEffect(() => {
      const currentScroller = scrollerRef.current;
      if (currentScroller) {
        currentScroller.addEventListener('wheel', handleWheel, {
          passive: false
        });
      }

      return () => {
        if (currentScroller) {
          currentScroller.removeEventListener('wheel', handleWheel);
        }
      };
    }, [getScroller]);

    const horizontal = direction === 'horizontal';
    const combinedStyle = {
      overflowX: horizontal ? 'auto' : 'hidden',
      overflowY: horizontal ? 'hidden' : 'auto',
      ...style
    };

    return (
      <div ref={combinedRef} className={className} style={combinedStyle}>
        {children}
      </div>
    );
  }
);

ScrollingContainer.displayName = 'ScrollingContainer';

const propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  direction: PropTypes.string,
  getScroller: PropTypes.func,
  style: PropTypes.object
};

ScrollingContainer.propTypes = propTypes;

export default ScrollingContainer;
